/**
 * Behavior hook for the OR-6126 preview "Concepts" tab.
 *
 * Owns the flat concept-list view state — debounced search, standard/non-standard
 * filter, paging, and the CSV download — feeding the shared
 * ``usePreviewConcepts`` query. Lifted out of the tab component so the tab renders
 * purely from props and the view behavior is unit-testable on its own.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */

import type { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  serializeConceptsFilter,
  serializeConceptsSort,
} from '@verantos-dev/feature-concept-group-detail';
import {
  downloadPreviewCsv,
  usePreviewConcepts,
  type PreviewConcept,
  type PreviewStandardFilter,
} from '@verantos-dev/feature-epi-chat';

const SEARCH_DEBOUNCE_MS = 350;
export const PREVIEW_CONCEPTS_PAGE_SIZE = 50;

// Match the details dialog's default ordering so the two surfaces open the same.
const DEFAULT_SORT_MODEL: GridSortModel = [{ field: 'conceptName', sort: 'asc' }];

export interface PreviewConceptsTableModel {
  searchInput: string;
  setSearchInput: (value: string) => void;
  standardFilter: PreviewStandardFilter;
  setStandardFilter: (value: PreviewStandardFilter) => void;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  filterModel: GridFilterModel;
  onFilterModelChange: (model: GridFilterModel) => void;
  page: number; // 0-based, for TablePagination
  setPage: (page: number) => void;
  pageSize: number;
  concepts: PreviewConcept[];
  total: number;
  isLoading: boolean;
  /** A refetch (search / filter / page change) is in flight — drives the grid overlay. */
  isFetching: boolean;
  isError: boolean;
  downloading: boolean;
  handleDownload: () => Promise<void>;
}

export const usePreviewConceptsTable = ({
  previewId,
  groupName,
}: {
  previewId: string | null;
  groupName: string;
}): PreviewConceptsTableModel => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [standardFilter, setStandardFilterState] = useState<PreviewStandardFilter>('all');
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [page, setPage] = useState(0);
  const [downloading, setDownloading] = useState(false);

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  const conceptsQuery = usePreviewConcepts({
    previewId,
    page: page + 1,
    pageSize: PREVIEW_CONCEPTS_PAGE_SIZE,
    search,
    standardFilter,
    sort: serializeConceptsSort(sortModel),
    filter: serializeConceptsFilter(filterModel),
  });

  // Memoize so the ``?? []`` fallback keeps a stable identity across renders.
  const concepts = useMemo(
    () => conceptsQuery.data?.concepts ?? [],
    [conceptsQuery.data?.concepts]
  );
  const total = conceptsQuery.data?.total ?? 0;

  const setStandardFilter = useCallback((value: PreviewStandardFilter) => {
    setStandardFilterState(value);
    setPage(0);
  }, []);

  // Sort / filter are server-side, so a change must reset to the first page.
  const onSortModelChange = useCallback((next: GridSortModel) => {
    setSortModel(next);
    setPage(0);
  }, []);

  const onFilterModelChange = useCallback((next: GridFilterModel) => {
    setFilterModel(next);
    setPage(0);
  }, []);

  const handleDownload = useCallback(async () => {
    if (!previewId) return;
    setDownloading(true);
    try {
      await downloadPreviewCsv(previewId, groupName);
    } finally {
      setDownloading(false);
    }
  }, [previewId, groupName]);

  return {
    searchInput,
    setSearchInput,
    standardFilter,
    setStandardFilter,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
    page,
    setPage,
    pageSize: PREVIEW_CONCEPTS_PAGE_SIZE,
    concepts,
    total,
    isLoading: conceptsQuery.isLoading,
    isFetching: conceptsQuery.isFetching,
    isError: conceptsQuery.isError,
    downloading,
    handleDownload,
  };
};
